import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import api from "../../services/api";

// Plans look: sizes and colours on the app's Plans screens. Open apps pick up a save
// right away, so none of this needs a new build. The server holds defaults and limits.
const GROUPS = [
  {
    key: "hostCorner",
    title: "Host circle on the Plans flyer",
    note: "In points on a 358 wide flyer. Everything scales with the flyer on bigger and smaller phones.",
    fields: [
      ["soloDot", "Host photo circle"],
      ["soloName", "Host name size"],
      ["soloBadge", "Verified badge size"],
      ["soloRole", "Role text size (DJ, Promoter)"],
      ["inset", "Space from the flyer edge"],
      ["gap", "Space between photo and name"],
      ["pairDot", "Photo circle with a cohost"],
      ["pairOverlap", "How much the two circles overlap"],
      ["pairName", "Name size with a cohost"],
    ],
  },
  {
    key: "hostTile",
    title: "Hosted by photos on the plan page",
    note: "Photo height and name size are for a 172 wide photo and scale with the photo width.",
    fields: [
      ["perRow", "How many photos fit across the screen (3 is a third each)"],
      ["gap", "Space between photos"],
      ["height", "Photo height"],
      ["name", "Name size"],
    ],
  },
  {
    key: "guestPhoto",
    title: "Guest photo size",
    note: "Guests and requests on the plan page. A corner roundness of half the size makes a circle.",
    fields: [
      ["size", "Photo size"],
      ["radius", "Corner roundness"],
    ],
  },
  {
    key: "profileCard",
    title: "Plans row on a profile",
    fields: [
      ["width", "Card width"],
      ["height", "Card height"],
      ["inset", "Space from the screen edge"],
    ],
  },
  {
    key: "homeCard",
    title: "Venue events row on Home",
    fields: [
      ["width", "Card width"],
      ["height", "Card height"],
    ],
  },
  {
    key: "pager",
    title: "Plans pager page",
    fields: [
      ["titleLines", "Title lines"],
      ["descriptionLines", "Description lines (0 hides it)"],
      ["viewMoreOpacity", "View more fade when pressed (0.1 to 1)"],
    ],
  },
  {
    key: "joinFallback",
    title: "I'm in button colour",
    note: "Used when the theme itself sets no I'm in colour above. Text turns dark or light on its own to stay readable.",
    fields: [
      ["zona", "ZONA"],
      ["trippy", "Trippy"],
      ["cyber", "Cyber"],
      ["wave", "Soundwave"],
      ["noir", "Noir"],
      ["smoke", "Smoke"],
      ["none", "Plans with no theme colour"],
    ],
  },
];

const HEX = /^#[0-9A-Fa-f]{6}$/;
const same = (a, b) => String(a).toUpperCase() === String(b).toUpperCase();

// Why a draft can't be saved, or "" when it can. Mirrors the server's check.
function problem(limit, draft) {
  if (!limit) return "";
  if (limit.color) return HEX.test(String(draft).trim()) ? "" : "Use a colour like #FFFFFF";
  const s = String(draft).trim();
  const n = Number(s);
  if (s === "" || !Number.isFinite(n)) return "Enter a number";
  if (limit.int && !Number.isInteger(n)) return "Whole numbers only";
  if (n < limit.min || n > limit.max) return `Between ${limit.min} and ${limit.max}`;
  return "";
}

const toValue = (limit, draft) => (limit?.color ? String(draft).trim().toUpperCase() : Number(String(draft).trim()));

export default function PlansLook() {
  const [server, setServer] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/app-config/plan-ui");
      setServer(data);
      setDraft(JSON.parse(JSON.stringify(data.planUi)));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not load the Plans look");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Only groups and fields the server knows, so a newer page never crashes on an older server.
  const groups = useMemo(() => {
    if (!server) return [];
    const known = (g, f) => server.defaults?.[g]?.[f] !== undefined && server.planUi?.[g]?.[f] !== undefined;
    return GROUPS.map((g) => ({ ...g, fields: g.fields.filter(([f]) => known(g.key, f)) })).filter((g) => g.fields.length);
  }, [server]);

  const changes = useMemo(() => {
    if (!server || !draft) return { body: {}, count: 0, invalid: 0 };
    const body = {};
    let count = 0;
    let invalid = 0;
    for (const g of groups) {
      for (const [f] of g.fields) {
        const limit = server.limits?.[g.key]?.[f];
        const d = draft[g.key]?.[f];
        if (problem(limit, d)) { invalid += 1; continue; }
        const v = toValue(limit, d);
        if (same(v, server.planUi[g.key][f])) continue;
        // Back to the default clears the stored value, so a later default change still reaches it.
        body[g.key] = { ...(body[g.key] || {}), [f]: same(v, server.defaults[g.key][f]) ? null : v };
        count += 1;
      }
    }
    return { body, count, invalid };
  }, [server, draft, groups]);

  const setField = (g, f, v) => setDraft((prev) => ({ ...prev, [g]: { ...prev[g], [f]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/app-config/plan-ui", changes.body);
      setServer((prev) => ({ ...prev, planUi: data.planUi }));
      setDraft(JSON.parse(JSON.stringify(data.planUi)));
      toast.success(`Saved. Open apps update now.`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!server || !draft) {
    return (
      <div className="pl">
        <h3 className="pt__subtitle">Plans look</h3>
        <p className="pt__note">Loading...</p>
      </div>
    );
  }

  return (
    <div className="pl">
      <div className="pt__header pt__header--fonts">
        <h3 className="pt__subtitle">Plans look</h3>
        <div className="pl__actions">
          {changes.count > 0 && (
            <button className="pt__secondary" disabled={saving} onClick={() => setDraft(JSON.parse(JSON.stringify(server.planUi)))}>
              Undo changes
            </button>
          )}
          <button className="pt__primary" disabled={saving || !changes.count || changes.invalid > 0} onClick={save}>
            {saving ? "Saving..." : changes.count ? `Save ${changes.count} ${changes.count === 1 ? "change" : "changes"}` : "Save"}
          </button>
        </div>
      </div>
      <p className="pt__hint">
        Sizes and colours on the Plans screens. Open apps refresh as soon as you save, and every other
        phone picks it up the next time the app opens. No new build needed. Sizes are in points.
        If the app ever gets a value it can't use, it falls back to the default for that field.
      </p>

      <div className="pl__groups">
        {groups.map((g) => (
          <div key={g.key} className="pl__group">
            <div className="pl__group-title">{g.title}</div>
            {g.note && <div className="pt__note pl__group-note">{g.note}</div>}
            {g.fields.map(([f, label]) => {
              const limit = server.limits?.[g.key]?.[f];
              const def = server.defaults[g.key][f];
              const d = draft[g.key][f];
              const err = problem(limit, d);
              const edited = !err && !same(toValue(limit, d), server.planUi[g.key][f]);
              const atDefault = !err && same(toValue(limit, d), def);
              return (
                <div key={f} className="pl__row">
                  <label className="pl__label" htmlFor={`pl-${g.key}-${f}`}>{label}</label>
                  <div className="pl__control">
                    {limit?.color && (
                      <input
                        type="color"
                        aria-label={`${label} colour`}
                        value={HEX.test(String(d)) ? String(d).toLowerCase() : "#ffffff"}
                        onChange={(e) => setField(g.key, f, e.target.value.toUpperCase())}
                      />
                    )}
                    <input
                      id={`pl-${g.key}-${f}`}
                      className={`pl__input${err ? " pl__input--bad" : ""}${edited ? " pl__input--edited" : ""}`}
                      type={limit?.color ? "text" : "number"}
                      step={limit?.int ? 1 : "any"}
                      min={limit?.min}
                      max={limit?.max}
                      value={d}
                      onChange={(e) => setField(g.key, f, e.target.value)}
                    />
                    <span className="pl__default">Default {def}</span>
                    <button
                      type="button"
                      className="pt__link"
                      disabled={atDefault}
                      onClick={() => setField(g.key, f, def)}>
                      Reset to default
                    </button>
                  </div>
                  {err && <div className="pt__error pl__err">{err}</div>}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
